// What every signed-in page shares: its access gate, its error pages and their way home.
// Under /view-as/$userId these are the viewed user's (their /api/me, and home keeps the
// prefix).
import type { ReactNode } from 'react';
import { useMe } from '../api/queries/me';
import { useTenant } from '../api/queries/tenant';
import { accessTo } from '../auth/access';
import { Forbidden, NotFound, UnexpectedError } from './Page';
import type { PageKey } from './pages';
import { useViewAsHref } from './viewAs';

export const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
export const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The caller's default route: where Go to home goes. */
export function useHome(): string {
  const { data: me } = useMe();
  return useViewAsHref(me?.defaultRoute ?? '/');
}

/** The page if the user's sections and the tenant's features open it; 403 or 404 if not. */
export function PageGate({ page, children }: { page: PageKey; children: ReactNode }) {
  const { data: me } = useMe();
  const { data: tenant } = useTenant();
  const home = useHome();
  if (!me || !tenant) return null;
  const access = accessTo(page, me.sections, tenant.features);
  if (access === 'forbidden') return <Forbidden home={home} />;
  if (access === 'off') return <NotFound home={home} />;
  return children;
}

/** The 404 page, inside the shell. */
export function InShellNotFound() {
  return <NotFound home={useHome()} />;
}

export function InShellError({ error }: { error: unknown }) {
  return <UnexpectedError error={error} home={useHome()} />;
}
